import {
    BillingPeriodId,
    BillingPeriodIdMap,
    defaultCommitmentPeriods,
    defaultTrialPeriods,
    EndMembershipReason,
    EndMembershipReasonMap,
} from "@albertine/types";
import { format, parseISO } from "date-fns";
import { Button } from "@/components/ui/button";
import type { MembershipOption, Profile } from "../../data/profile";
import { useMemberProfile } from "../../MemberProfileContext";
import { FieldList } from "../../profilePrimitives";
import { FormShell } from "../ProfileOverlayShell";

export function MembershipDetails({
    onClose,
    onCancelRenewal,
}: {
    onClose: () => void;
    onCancelRenewal: () => void;
}) {
    const { profile, membershipTiers } = useMemberProfile();
    const cancelled = profile.membership.cancellation != null;

    return (
        <FormShell
            title="Membership details"
            onClose={onClose}
            footer={
                cancelled ? undefined : (
                    <div className="flex w-full flex-col gap-2">
                        <Button
                            type="button"
                            variant="destructive"
                            className="w-full"
                            onClick={onCancelRenewal}
                        >
                            Cancel membership renewal
                        </Button>
                        <Button
                            type="button"
                            variant="outline"
                            className="w-full"
                            onClick={onClose}
                        >
                            Close
                        </Button>
                    </div>
                )
            }
        >
            <FieldList rows={membershipDetailRows(profile, membershipTiers)} />
        </FormShell>
    );
}

function membershipDetailRows(
    profile: Profile,
    membershipTiers: readonly MembershipOption[],
) {
    const { membership } = profile;
    const onTrial = !!membership.trial;
    const cancellationReason = EndMembershipReason.safeParse(
        membership.cancellation?.reason,
    );
    const billingPeriod = BillingPeriodId.safeParse(membership.billingPeriod);

    const rows: { label: string; value: string }[] = [
        {
            label: "Tier",
            value:
                membershipTiers.find((option) => option.id === membership.tier)?.name ??
                "Not set",
        },
    ];

    const status = [
        membership.trial ? "Trial" : undefined,
        membership.expired ? "Expired" : undefined,
    ].filter(Boolean);
    if (status.length) {
        rows.push({ label: "Status", value: status.join(" · ") });
    }

    const trialOption = defaultTrialPeriods.find(
        (option) => option.id === membership.trialPeriod,
    );
    if (trialOption) {
        rows.push({ label: "Trial", value: trialOption.name });
    }
    const durationOption = defaultCommitmentPeriods.find(
        (option) => option.id === membership.commitmentPeriod,
    );
    if (durationOption) {
        rows.push({ label: "Duration", value: durationOption.name });
    }
    if (membership.billingPeriod) {
        rows.push({
            label: "Billing frequency",
            value: billingPeriod.success
                ? BillingPeriodIdMap[billingPeriod.data]
                : membership.billingPeriod,
        });
    }
    if (membership.startsOn) {
        rows.push({
            label: "Start date",
            value: format(parseISO(membership.startsOn), "d MMM yyyy"),
        });
    }
    if (onTrial && membership.trialEndsOn) {
        rows.push({
            label: "Renewal date",
            value: format(parseISO(membership.trialEndsOn), "d MMM yyyy"),
        });
    }
    if (!onTrial && membership.endsOn) {
        rows.push({
            label: "Renewal date",
            value: format(parseISO(membership.endsOn), "d MMM yyyy"),
        });
    }
    if (membership.statusNote) {
        rows.push({ label: "Note", value: membership.statusNote });
    }
    if (membership.cancellation) {
        rows.push({
            label: "Reason",
            value: cancellationReason.success
                ? EndMembershipReasonMap[cancellationReason.data]
                : membership.cancellation.reason,
        });
    }
    if (membership.cancellation?.additionalNote) {
        rows.push({
            label: "Notes",
            value: membership.cancellation.additionalNote,
        });
    }

    return rows;
}
